import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
//icons
import { Link } from 'react-router-dom';

import { RowNode } from 'ag-grid-community';

import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';
import ICONS from '../../../constants/icons';
import routes from '../../../constants/routes';

import { createShouldForwardProp } from 'helpers/shouldForwardProp';

import useGetLocationState from 'hooks/helper/useGetLocationState';
import { useAuthSession } from 'hooks/useAuthSession';

import WarningIcon from '../../../images/WarningIcon.svg?react';

// models
import { FormulaDetailModel } from '../../../models/formula.model';
import { UserRole } from 'models/user.model';

// components
import AampeTooltip from '../../shared/AampeTooltip/AampeTooltip';

interface FormulaNameTableCellProps {
    data: FormulaDetailModel;
    isDrawerOpen: boolean; // parent tells this row if its drawer is open
    onDrawerOpen: () => void;
    node: RowNode;
}
const shouldForwardProps = createShouldForwardProp(['visible']);
const FormulaNameTableCell: FC<
    React.PropsWithChildren<FormulaNameTableCellProps>
> = ({ data, isDrawerOpen, onDrawerOpen, node }) => {
    const { user } = useAuthSession();

    const [hover, setHover] = useState<boolean>(false);
    const shouldShowIcon =
        hover || isDrawerOpen || (node.isSelected() as boolean);
    const [isTruncated, setIsTruncated] = useState<boolean>(false);
    const nameRef = useRef<HTMLAnchorElement>(null);
    const handlePreviewClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (node) {
            node.setSelected(true, true);
        }
        onDrawerOpen();
    };

    const { getLocationState } = useGetLocationState('All Messages');

    const messageLinkTo = useMemo(
        () => ({
            pathname: routes.formulaView(data.active_state, data.id),
            state: getLocationState(),
        }),
        [data.active_state, data.id, getLocationState],
    );

    useEffect(() => {
        const el = nameRef.current;
        if (el && el.scrollWidth > el.clientWidth) {
            setIsTruncated(true);
        } else {
            setIsTruncated(false);
        }
    }, [data.name]);

    const messageNameContent = (
        <MessageNameLink
            ref={nameRef}
            to={messageLinkTo}
            onClick={(e) => {
                e.stopPropagation();
            }}
            data-testid="message-name-link"
        >
            {data.name || 'Untitled Message'}
        </MessageNameLink>
    );
    return (
        <CellWrapper
            className="drawer-click-exempt" // this prevents clicking on a row from closing the drawer
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            data-testid="preview-icon-trigger"
        >
            <Container>
                <NameWrapper>
                    {user?.role == UserRole.EDITOR && data?.has_write_access && (
                        <AampeTooltip
                            placement="top"
                            arrow={true}
                            title={'You have access to edit this message'}
                        >
                            <IconDiv>
                                <PencilIcon />
                            </IconDiv>
                        </AampeTooltip>
                    )}
                    {user?.role == UserRole.EDITOR && !data?.has_write_access && (
                        <AampeTooltip
                            placement="top"
                            arrow={true}
                            title={'You can only view this message'}
                        >
                            <IconDiv>
                                <EyeIcon />
                            </IconDiv>
                        </AampeTooltip>
                    )}
                    {isTruncated ? (
                        <AampeTooltip title={data.name || 'Untitled Message'}>
                            {messageNameContent}
                        </AampeTooltip>
                    ) : (
                        messageNameContent
                    )}
                    {data?.is_in_edit_mode && (
                        <WarningIcon width={35} height={35} />
                    )}
                </NameWrapper>
                <IconWrapper
                    visible={shouldShowIcon}
                    className="preview-icon"
                    onClick={handlePreviewClick}
                    data-testid="preview-icon"
                >
                    <ICONS.sidebar />
                </IconWrapper>
            </Container>
        </CellWrapper>
    );
};

const Container = styled.div({
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    position: 'relative',
    width: '100%',
});

const CellWrapper = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
});

const NameWrapper = styled.div({
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    maxWidth: '100%',
});

const MessageNameLink = styled(Link)({
    color: aampeTheme.colors.neutral09,
    fontWeight: aampeTheme.weights.light,
    textDecoration: 'none',
    cursor: 'pointer',
    alignItems: 'center',
    display: 'inline-block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    '&:hover': {
        textDecoration: 'underline',
    },
});

const IconWrapper = styled.div.withConfig({
    shouldForwardProp: shouldForwardProps,
})<{ visible: boolean }>(({ visible }) => ({
    opacity: visible ? 1 : 0,
    transition: 'opacity 0.2s ease',
    cursor: 'pointer',
    marginLeft: '8px',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    width: '24px',
    visibility: visible ? 'visible' : 'hidden',
}));
const IconDiv = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});
const commonStyle = {
    width: '20px',
    height: '20px',
    '&:hover': {
        color: aampeTheme.colors.solidA02,
    },
};
const PencilIcon = styled(ICONS.thinLightPencil)({
    ...commonStyle,
});

const EyeIcon = styled(ICONS.thinLightEye)({
    ...commonStyle,
});
export default FormulaNameTableCell;
